import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import CTASection from '../components/CTASection';
import { Settings, Zap, Link2, Headphones, ArrowRight, CheckCircle2 } from 'lucide-react';

const Diensten = () => {
  const services = [
    {
      icon: <Settings className="w-12 h-12 text-blue-700" />,
      title: 'CRM Implementatie',
      description: 'Complete implementatie van Zoho CRM of andere CRM systemen afgestemd op uw bedrijfsprocessen. Van requirements analyse tot go-live begeleiding.',
      features: [
        'Requirement analyse en advies',
        'Systeem configuratie en customisatie',
        'Data migratie vanuit bestaande systemen',
        'User training en change management',
        'Go-live support en nazorg',
      ],
      link: '/zoho-crm',
    },
    {
      icon: <Zap className="w-12 h-12 text-blue-700" />,
      title: 'Automatisering & Workflows',
      description: 'Bespaar tijd en verhoog efficiency door repetitieve processen te automatiseren. Wij bouwen slimme workflows die uw team ontlasten.',
      features: [
        'Proces analyse en optimalisatie',
        'Workflow design en implementatie',
        'Email automatisering en templates',
        'Automatische taken en herinneringen',
        'Custom scripts en functies',
      ],
      link: '/contact',
    },
    {
      icon: <Link2 className="w-12 h-12 text-blue-700" />,
      title: 'Integraties',
      description: 'Koppel uw CRM aan andere systemen voor één centrale waarheid. Wij bouwen naadloze integraties met boekhouding, marketing tools en meer.',
      features: [
        'Zoho Books & financiële systemen',
        'Marketing automation platforms',
        'Telefonie en communicatie tools',
        'E-commerce en webshop platforms',
        'Custom API koppelingen',
      ],
      link: '/contact',
    },
    {
      icon: <Headphones className="w-12 h-12 text-blue-700" />,
      title: 'Consultancy & Support',
      description: 'Strategisch advies voor optimaal gebruik van uw CRM. Ook na implementatie staan wij voor u klaar met support en doorlopende optimalisaties.',
      features: [
        'Strategisch CRM advies',
        'Proces optimalisatie',
        'Performance monitoring',
        'Periodieke system reviews',
        'Support en troubleshooting',
      ],
      link: '/contact',
    },
  ];

  const approach = [
    {
      step: '1',
      title: 'Intake & Analyse',
      description: 'Wij starten met een grondige analyse van uw huidige situatie, processen en doelstellingen.',
    },
    {
      step: '2',
      title: 'Advies & Planning',
      description: 'Op basis van de analyse adviseren wij de beste oplossing en maken een gedetailleerd projectplan.',
    },
    {
      step: '3',
      title: 'Implementatie',
      description: 'Wij configureren, bouwen en testen de oplossing in nauwe samenwerking met uw team.',
    },
    {
      step: '4',
      title: 'Training & Go-Live',
      description: 'Uw team wordt grondig getraind en wij begeleiden de lancering van het nieuwe systeem.',
    },
    {
      step: '5',
      title: 'Support & Optimalisatie',
      description: 'Ook na go-live blijven wij betrokken met support en continue verbetering van uw systemen.',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden bg-gradient-to-br from-blue-50 via-white to-blue-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              Onze Diensten
            </h1>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              Van implementatie tot optimalisatie - wij begeleiden u in elke fase van uw CRM journey. 
              Ontdek hoe wij uw bedrijfsprocessen kunnen transformeren.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button 
                  size="lg" 
                  className="bg-blue-700 hover:bg-blue-800 text-white px-8 py-6 text-lg font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 group"
                >
                  Vraag Advies Aan
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
                </Button>
              </Link>
              
              <a href="tel:0638823471">
                <Button 
                  size="lg" 
                  variant="outline"
                  className="border-2 border-blue-700 text-blue-700 hover:bg-blue-50 px-8 py-6 text-lg font-semibold rounded-lg transition-all duration-300"
                >
                  Bel: 06 3882 3471
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-16">
            {services.map((service, index) => (
              <div key={`item-${index}`} className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
                <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                  <div className="mb-6">{service.icon}</div>
                  <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                    {service.title}
                  </h2>
                  <p className="text-lg text-gray-600 mb-6">
                    {service.description}
                  </p>
                  <Link to={service.link}>
                    <Button 
                      className="bg-blue-700 hover:bg-blue-800 text-white px-6 py-3 rounded-lg transition-all duration-300 group"
                    >
                      Meer informatie
                      <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
                    </Button>
                  </Link>
                </div>

                <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                  <Card className="border-2 border-blue-200 shadow-xl">
                    <CardContent className="p-8">
                      <h3 className="text-xl font-bold text-gray-900 mb-4">Wat wij leveren:</h3>
                      <ul className="space-y-3">
                        {service.features.map((feature, idx) => (
                          <li key={idx} className="flex items-start space-x-3">
                            <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                            <span className="text-gray-700">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Approach Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Onze Aanpak
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Wij volgen een bewezen methodiek die zorgt voor succesvolle implementaties en tevreden klanten.
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="space-y-8">
              {approach.map((item, index) => (
                <div key={`item-${index}`} className="flex items-start space-x-6">
                  <div className="flex-shrink-0 w-16 h-16 bg-blue-700 text-white rounded-full flex items-center justify-center text-2xl font-bold shadow-lg">
                    {item.step}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">{item.title}</h3>
                    <p className="text-lg text-gray-600">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Waarom Kiezen voor Guijs Consultancy?
            </h2>
            <p className="text-xl text-gray-600 mb-12">
              Wij combineren technische expertise met zakelijk inzicht voor implementaties die echt resultaat opleveren.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 text-left">
              <Card className="border-2 border-blue-200 shadow-lg">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Bewezen Expertise</h3>
                  <p className="text-gray-600">
                    Jarenlange ervaring met complexe CRM implementaties voor bedrijven van alle groottes.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2 border-blue-200 shadow-lg">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Persoonlijke Aanpak</h3>
                  <p className="text-gray-600">
                    Directe communicatie en persoonlijke aandacht voor uw project en organisatie.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2 border-blue-200 shadow-lg">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Snelle Implementatie</h3>
                  <p className="text-gray-600">
                    Door onze ervaring bent u binnen enkele weken operationeel met uw nieuwe systemen.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2 border-blue-200 shadow-lg">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Continue Ondersteuning</h3>
                  <p className="text-gray-600">
                    Ook na go-live staan wij voor u klaar met support en optimalisaties.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CTASection 
        title="Klaar om te Starten?"
        subtitle="Neem contact op voor een vrijblijvend gesprek over uw CRM uitdaging."
      />
    </div>
  );
};

export default Diensten;
